import { logActivity } from '../utils/activityLog';
import { fmtDate } from '../utils/format';
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, RefreshCw, MoreVertical, Copy, KeyRound, Trash2, Link2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { toast } from 'sonner';
import { getCurrentUser, hasPermission } from '../utils/roles';


export default function Affiliates() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === 'dark';
  const currentUser = getCurrentUser();
  const canViewAffiliate = hasPermission(currentUser, 'View Affiliates');
  const canCreateAffiliate = hasPermission(currentUser, 'Create Affiliate');
  const canEditAffiliate = hasPermission(currentUser, 'Edit Affiliate');
  const canDeleteAffiliate = hasPermission(currentUser, 'Delete Affiliate');
  const hasRowActions = canViewAffiliate || canEditAffiliate || canDeleteAffiliate;

  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editAff, setEditAff] = useState(null);
  const [form, setForm] = useState({ name: '', office: '' });
  const [officeOptions, setOfficeOptions] = useState([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  const [showChangeToken, setShowChangeToken] = useState(null);
  const [showIntegration, setShowIntegration] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);

  const cls = {
    card: `rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    input: `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`,
    label: `block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    th: `px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`,
    td: `px-3 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`,
    btn: `px-3 py-1.5 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors',
  };

  useEffect(() => { logActivity('view', 'affiliates', null, 'Viewed Affiliates list'); }, []);

  useEffect(() => { loadAffiliates(); }, [activeFilter]);

  useEffect(() => { loadOffices(); }, []);

  useEffect(() => {
    if (showModal && !editAff && !form.office && officeOptions.length === 1) {
      setForm(p => ({ ...p, office: officeOptions[0].name }));
    }
  }, [showModal, editAff, form.office, officeOptions]);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const loadAffiliates = async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeFilter !== null) params.active = activeFilter;
      if (search) params.search = search;
      const { data } = await api.get('/affiliates', { params });
      setAffiliates(data);
    } catch { toast.error(t('affiliates.failedLoad')); }
    finally { setLoading(false); }
  };

  const loadOffices = async () => {
    try {
      setOfficesLoading(true);
      const { data } = await api.get('/offices', { params: { active: 'true' } });
      setOfficeOptions(Array.isArray(data) ? data : []);
    } catch {
      toast.error(t('affiliates.failedLoadOffices'));
      setOfficeOptions([]);
    } finally {
      setOfficesLoading(false);
    }
  };

  const openNew = () => {
    if (!canCreateAffiliate) return;
    setEditAff(null);
    setForm({ name: '', office: officeOptions.length === 1 ? officeOptions[0].name : '' });
    setShowModal(true);
  };

  const openEdit = (a) => {
    if (!canEditAffiliate) return;
    setEditAff(a);
    setForm({ name: a.name, office: a.office || '' });
    setShowModal(true);
  };

  const saveAff = async () => {
    if (editAff && !canEditAffiliate) return;
    if (!editAff && !canCreateAffiliate) return;
    if (!form.name) { toast.error(t('affiliates.nameRequired')); return; }
    if (!form.office) { toast.error(t('affiliates.officeRequired')); return; }
    try {
      if (editAff) {
        await api.put(`/affiliates/${editAff.id}`, { name: form.name, office: form.office || null });
        toast.success(t('affiliates.updated'));
      } else {
        await api.post('/affiliates', { name: form.name, office: form.office || null });
        toast.success(t('affiliates.created'));
      }
      setShowModal(false);
      loadAffiliates();
    } catch (e) { toast.error(e.response?.data?.detail || t('affiliates.saveFailed')); }
  };

  const toggleAff = async (a) => {
    if (!canDeleteAffiliate) return;
    try {
      await api.patch(`/affiliates/${a.id}/toggle`);
      toast.success(a.active ? t('affiliates.disabled') : t('affiliates.enabled'));
      loadAffiliates();
    } catch { toast.error(t('affiliates.failed')); }
  };

  const confirmChangeToken = async () => {
    if (!canEditAffiliate) return;
    try {
      const { data } = await api.patch(`/affiliates/${showChangeToken.id}/change-token`);
      toast.success(t('affiliates.tokenChanged'));
      navigator.clipboard.writeText(data.apiToken).catch(() => {});
      setShowChangeToken(null);
      loadAffiliates();
    } catch { toast.error(t('affiliates.tokenChangeFailed')); }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token).then(() => toast.success(t('affiliates.tokenCopied'))).catch(() => toast.error(t('affiliates.copyFailed')));
  };

  const copyText = (text, label = t('affiliates.copied')) => {
    navigator.clipboard.writeText(text).then(() => toast.success(label)).catch(() => toast.error(t('affiliates.copyFailed')));
  };

  const toggleMenu = (event, affiliateId) => {
    if (openMenu === affiliateId) {
      setOpenMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 176;
    const menuHeight = 210;
    const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);
    const top = Math.min(rect.bottom + 6, window.innerHeight - menuHeight - 8);

    setMenuPosition({ top, left });
    setOpenMenu(affiliateId);
  };

  const getLeadUrl = (affiliate) => affiliate?.leadUrl || '';

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {canCreateAffiliate && (
            <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700">
              <Plus size={14} /> {t('affiliates.new')}
            </button>
          )}
          <button onClick={() => setActiveFilter(true)}
            className={`px-4 py-1.5 text-sm rounded-md border transition-colors ${activeFilter === true ? 'bg-green-600 text-white border-green-600' : isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            {t('affiliates.enabled')}
          </button>
          <button onClick={() => setActiveFilter(false)}
            className={`px-4 py-1.5 text-sm rounded-md border transition-colors ${activeFilter === false ? 'bg-red-600 text-white border-red-600' : isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            {t('affiliates.disabled')}
          </button>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadAffiliates()}
            placeholder={t('affiliates.searchPlaceholder')}
            className={`px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 w-52 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`}
          />
        </div>
        <button onClick={loadAffiliates} className={cls.btn}><RefreshCw size={14} /></button>
      </div>

      {/* Table */}
      <div className={`${cls.card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDark ? 'bg-[#0a0e1a] border-b border-gray-800' : 'bg-gray-50 border-b border-gray-200'}>
              <tr>
                <th className={cls.th}><input type="checkbox" className="w-3.5 h-3.5 accent-purple-500" /></th>
                {[
                  t('affiliates.columns.id'),
                  t('affiliates.columns.name'),
                  t('affiliates.columns.office'),
                  t('affiliates.columns.status'),
                  t('affiliates.columns.apiToken'),
                  t('affiliates.columns.createdAt'),
                  t('affiliates.columns.actions'),
                ].map(h => (
                  <th key={h} className={cls.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={8} className={`${cls.td} text-center py-12`}>{t('affiliates.loading')}</td></tr>
              ) : affiliates.length === 0 ? (
                <tr><td colSpan={8} className={`${cls.td} text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('affiliates.empty')}</td></tr>
              ) : affiliates.map(a => (
                <tr key={a.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                  <td className={cls.td}><input type="checkbox" className="w-3.5 h-3.5 accent-purple-500" /></td>
                  <td className={cls.td}>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>#{a.id}</span>
                  </td>
                  <td className={`${cls.td} font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{a.name}</td>
                  <td className={cls.td}>{a.office || '—'}</td>
                  <td className={cls.td}>
                    <button
                      onClick={() => toggleAff(a)}
                      disabled={!canDeleteAffiliate}
                      className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${a.active ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'}`}>
                      {a.active ? t('affiliates.enabled') : t('affiliates.disabled')}
                    </button>
                  </td>
                  <td className={cls.td}>
                    <span className={`text-xs font-mono truncate block max-w-[260px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`} title={a.apiToken}>
                      {a.apiToken}
                    </span>
                  </td>
                  <td className={cls.td}>{fmtDate(a.createdAt)}</td>
                  <td className={cls.td}>
                    <div className="relative" ref={openMenu === a.id ? menuRef : null}>
                      {hasRowActions && (
                        <button onClick={(event) => toggleMenu(event, a.id)}
                          className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
                          <MoreVertical size={15} />
                        </button>
                      )}
                      {openMenu === a.id && (
                        <div
                          style={{ top: menuPosition.top, left: menuPosition.left }}
                          className={`fixed w-44 rounded-md shadow-lg border z-50 overflow-hidden ${isDark ? 'bg-[#1a1f3a] border-gray-700' : 'bg-white border-gray-200'}`}
                        >
                          {[
                            ...(canEditAffiliate ? [
                              { icon: Pencil, label: t('affiliates.edit'), action: () => { openEdit(a); setOpenMenu(null); } },
                            ] : []),
                            ...(canViewAffiliate ? [
                              { icon: Link2, label: t('affiliates.integrationInfo'), action: () => { setShowIntegration(a); setOpenMenu(null); } },
                              { icon: Copy, label: t('affiliates.copyToken'), action: () => { copyToken(a.apiToken); setOpenMenu(null); } },
                            ] : []),
                            ...(canEditAffiliate ? [
                              { icon: KeyRound, label: t('affiliates.changeToken'), action: () => { setShowChangeToken(a); setOpenMenu(null); } },
                            ] : []),
                            ...(canDeleteAffiliate ? [
                              { icon: Trash2, label: a.active ? t('affiliates.disabled') : t('affiliates.enabled'), action: () => { toggleAff(a); setOpenMenu(null); } },
                            ] : []),
                          ].map(({ icon: Icon, label, action }) => (
                            <button key={label} onClick={action}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-[#2a2f4a]' : 'text-gray-700 hover:bg-gray-50'}`}>
                              <Icon size={13} />{label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
          {t('affiliates.showing')} 1 {t('affiliates.to')} {affiliates.length} {t('affiliates.of')} {affiliates.length} {t('affiliates.entries')}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-sm rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {editAff ? t('affiliates.editAffiliate') : t('affiliates.addAffiliate')}
              </h2>
              <button onClick={() => setShowModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500'}>✕</button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div>
                <label className={cls.label}>{t('affiliates.name')} *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className={cls.input} placeholder={t('affiliates.namePlaceholder')} />
              </div>
              <div>
                <label className={cls.label}>{t('affiliates.office')}</label>
                <select
                  value={form.office}
                  onChange={e => setForm(p => ({ ...p, office: e.target.value }))}
                  disabled={officesLoading || officeOptions.length === 0}
                  className={`${cls.input} disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  <option value="">
                    {officesLoading ? t('affiliates.loading') : t('affiliates.officePlaceholder')}
                  </option>
                  {officeOptions.map(office => (
                    <option key={office.id} value={office.name}>{office.name}</option>
                  ))}
                </select>
                {!officesLoading && officeOptions.length === 0 && (
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('affiliates.noOffices')}</p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowModal(false)} className={cls.btn}>{t('affiliates.cancel')}</button>
                <button onClick={saveAff} className={cls.btnPrimary}>{t('affiliates.save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integration Info Modal */}
      {showIntegration && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-2xl rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('affiliates.integration')} - {showIntegration.name}
              </h2>
              <button onClick={() => setShowIntegration(null)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500'}>✕</button>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[80vh]">

              {/* Endpoint URL */}
              <div>
                <p className={`text-xs font-medium mb-1 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('affiliates.leadEndpoint')}</p>
                <div className={`flex min-w-0 items-center gap-2 overflow-hidden p-2.5 rounded-md border font-mono text-xs ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-green-400' : 'bg-gray-50 border-gray-200 text-green-700'}`}>
                  <span className="min-w-0 flex-1 break-all">POST {getLeadUrl(showIntegration)}</span>
                  <button onClick={() => copyText(getLeadUrl(showIntegration), t('affiliates.urlCopied'))}
                    className={`shrink-0 p-1 rounded hover:bg-gray-700/50 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}>
                    <Copy size={13} />
                  </button>
                </div>
              </div>

              {/* Request format */}
              <div>
                <p className={`text-xs font-medium mb-1 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('affiliates.requestBody')}</p>
                <div className={`overflow-hidden p-3 rounded-md border font-mono text-xs ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                  <pre className="whitespace-pre-wrap break-words">{JSON.stringify({
                    "id":         "(optional) your order ID for deduplication",
                    "firstName":  "John",
                    "lastName":   "Doe",
                    "email":      "john@example.com",
                    "phone":      "79876543210",
                    "ip":         "1.2.3.4",
                    "affiliateId": "(optional) affiliate lead ID",
                    "country":    "RU",
                    "language":   "ru",
                    "department": "Sales",
                    "source":     "(optional) source",
                    "referral":   "(optional) referral",
                    "funnel":     "(optional) funnel",
                    "us":         "(optional) utm_source",
                    "uc":         "(optional) utm_campaign",
                    "um":         "(optional) utm_medium",
                    "un":         "(optional) utm_content",
                    "ut":         "(optional) utm_term"
                  }, null, 2)}</pre>
                </div>
                <button onClick={() => copyText(`curl -X POST "${getLeadUrl(showIntegration)}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"firstName":"John","lastName":"Doe","email":"john@example.com","phone":"79876543210","ip":"1.2.3.4","country":"RU","language":"ru","department":"Sales"}'`, t('affiliates.curlCopied'))}
                  className="mt-1.5 text-xs text-purple-400 hover:text-purple-300">{t('affiliates.copyCurl')}</button>
              </div>

              {/* Response format */}
              <div>
                <p className={`text-xs font-medium mb-1 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('affiliates.response')}</p>
                <div className={`overflow-hidden p-3 rounded-md border font-mono text-xs space-y-1 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                  <div className="text-green-400">{"// Success"}</div>
                  <pre className="whitespace-pre-wrap break-words">{"{ \"status\": \"ok\", \"uid\": 42, \"leadId\": 42, \"id\": \"your-order-id\" }"}</pre>
                  <div className={`mt-2 ${isDark ? 'text-red-400' : 'text-red-500'}`}>{"// Errors"}</div>
                  <pre className="whitespace-pre-wrap break-words">{"{ \"status\": \"error\", \"error\": \"duplicate\" }  // email, phone or external ID already exists"}</pre>
                  <pre className="whitespace-pre-wrap break-words">{"{ \"status\": \"error\", \"error\": \"notoken\"   }  // invalid/inactive token"}</pre>
                  <pre className="whitespace-pre-wrap break-words">{"{ \"status\": \"error\", \"error\": \"noemail\"   }  // email missing"}</pre>
                  <pre className="whitespace-pre-wrap break-words">{"{ \"status\": \"error\", \"error\": \"nophone\"   }  // phone missing"}</pre>
                </div>
              </div>

              {/* API Token */}
              <div>
                <p className={`text-xs font-medium mb-1 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>API Token</p>
                <div className={`flex min-w-0 items-center gap-2 overflow-hidden p-2.5 rounded-md border font-mono text-xs ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                  <span className="min-w-0 flex-1 break-all">{showIntegration.apiToken}</span>
                  <button onClick={() => copyText(showIntegration.apiToken, t('affiliates.tokenCopied'))}
                    className={`shrink-0 p-1 rounded ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}>
                    <Copy size={13} />
                  </button>
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <button onClick={() => setShowIntegration(null)} className={cls.btn}>{t('affiliates.close')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Token Confirm */}
      {showChangeToken && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-sm rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('affiliates.changeToken')}</h2>
              <button onClick={() => setShowChangeToken(null)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500'}>✕</button>
            </div>
            <div className="px-6 py-5">
              <p className={`text-sm mb-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('affiliates.changeTokenConfirm')}
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowChangeToken(null)} className={cls.btn}>{t('affiliates.cancel')}</button>
                <button onClick={confirmChangeToken} className={cls.btnPrimary}>{t('affiliates.confirm')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
